import { ActionRail } from "@/components/advisor/ActionRail";
import { DocumentsSection } from "@/components/advisor/DocumentsSection";
import {
	RedFlagRegister,
	TensionList,
} from "@/components/advisor/FindingsPanels";
import { LensOpinionCard } from "@/components/advisor/LensOpinionCard";
import { StageStepper } from "@/components/advisor/StageStepper";
import { SynthesisPanel } from "@/components/advisor/SynthesisPanel";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { LoadingState } from "@/components/ui/loading-state";
import {
	useCancelEngagement,
	useDossier,
	useEngagement,
	useEngagementStream,
	useLensOpinions,
} from "@/hooks/use-advisor";
import { advisorClient } from "@/lib/advisor";
import { useMutation, useQueryClient } from "@tanstack/react-query";
import { createFileRoute } from "@tanstack/react-router";
import { useTranslation } from "react-i18next";
import { toast } from "sonner";

export const Route = createFileRoute("/advisor/$id")({
	component: EngagementDetail,
});

interface FramingJson {
	scope?: string;
	assumptions?: string[];
	outOfScope?: string[];
}

function FramingCard({ framing }: { framing: FramingJson }) {
	const { t } = useTranslation("advisor");
	return (
		<Card>
			<CardHeader className="py-3">
				<CardTitle className="text-sm">{t("framing_title")}</CardTitle>
			</CardHeader>
			<CardContent className="pt-0 space-y-2 text-sm">
				{framing.scope && (
					<div>
						<div className="font-medium">{t("framing_scope")}</div>
						<p className="text-muted-foreground">{framing.scope}</p>
					</div>
				)}
				{(framing.assumptions?.length ?? 0) > 0 && (
					<div>
						<div className="font-medium">{t("framing_assumptions")}</div>
						<ul className="list-disc pl-4 text-muted-foreground">
							{framing.assumptions?.map((a) => (
								<li key={a}>{a}</li>
							))}
						</ul>
					</div>
				)}
				{(framing.outOfScope?.length ?? 0) > 0 && (
					<div>
						<div className="font-medium">{t("framing_out_of_scope")}</div>
						<ul className="list-disc pl-4 text-muted-foreground">
							{framing.outOfScope?.map((a) => (
								<li key={a}>{a}</li>
							))}
						</ul>
					</div>
				)}
			</CardContent>
		</Card>
	);
}

function EngagementDetail() {
	const { id } = Route.useParams();
	const { t } = useTranslation("advisor");
	const qc = useQueryClient();
	useEngagementStream(id);

	const { data: engagement, isLoading } = useEngagement(id);
	const cancel = useCancelEngagement(id);
	const { data: opinionsData } = useLensOpinions(id);
	const { data: dossier } = useDossier(id, engagement?.stage ?? "");

	const start = useMutation({
		mutationFn: () => advisorClient.startEngagement({ engagementId: id }),
		onSuccess: () => {
			toast.success(t("started"));
			qc.invalidateQueries({ queryKey: ["advisor"] });
		},
	});

	if (isLoading || !engagement) {
		return <LoadingState variant="spinner" />;
	}

	const opinions = opinionsData?.opinions ?? [];
	const framing = engagement.framing?.toJson() as FramingJson | null;
	const rounds = [...new Set(opinions.map((o) => o.round))].sort();
	const isDraft = engagement.status === "draft";
	const allowDocAdd = isDraft || engagement.status === "awaiting_evidence";

	return (
		<div className="p-6 space-y-4 max-w-6xl">
			<div className="space-y-2">
				<div className="flex items-center gap-3">
					<h1 className="text-lg font-semibold flex-1">{engagement.title}</h1>
					{["active", "awaiting_gate", "awaiting_evidence", "draft"].includes(
						engagement.status,
					) && (
						<Button
							size="sm"
							variant="outline"
							disabled={cancel.isPending}
							onClick={() =>
								cancel.mutate(undefined, {
									onSuccess: () => toast.success(t("cancelled")),
								})
							}
						>
							{t("cancel_engagement")}
						</Button>
					)}
				</div>
				<StageStepper stage={engagement.stage} status={engagement.status} />
			</div>

			{isDraft ? (
				<div className="max-w-md space-y-3">
					<DocumentsSection engagementId={id} allowAdd />
					<Button disabled={start.isPending} onClick={() => start.mutate()}>
						{t("start_engagement")}
					</Button>
				</div>
			) : (
				<div className="grid grid-cols-1 lg:grid-cols-[1fr_320px] gap-4">
					{/* Dossier column */}
					<div className="space-y-3 min-w-0">
						{dossier && <SynthesisPanel dossier={dossier} />}
						{framing?.scope && <FramingCard framing={framing} />}
						{rounds.map((round) => (
							<div key={round} className="space-y-2">
								<h3 className="text-sm font-medium">
									{t("opinions_title")} — {t("opinions_round", { round })}
								</h3>
								{opinions
									.filter((o) => o.round === round)
									.map((o) => (
										<LensOpinionCard key={`${o.round}-${o.lens}`} opinion={o} />
									))}
							</div>
						))}
						<TensionList engagementId={id} />
						<RedFlagRegister engagementId={id} />
						<DocumentsSection engagementId={id} allowAdd={allowDocAdd} />
					</div>

					{/* Action rail */}
					<div className="lg:sticky lg:top-4 self-start">
						<ActionRail engagement={engagement} />
					</div>
				</div>
			)}
		</div>
	);
}
